import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, ViewEncapsulation } from '@angular/core';
import { HxBadge } from '../badge/badge';
export interface DonutSegment {
  label: string;
  value: number;
  color: string;
}

export type DonutSize = 'sm' | 'md' | 'lg';
export type DonutLegendVariant = 'plain' | 'split';

/**
 * Donut chart displaying segmented data with an optional legend.
 *
 * @example
 *   ```html
 *   <hx-donut-chart [segments]="segments" size="md"></hx-donut-chart>
 *   ```;
 */
@Component({
  selector: 'hx-donut-chart',
  standalone: true,
  imports: [CommonModule, HxBadge],
  templateUrl: './donut-chart.html',
  styleUrl: './donut-chart.css',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HxDonutChart {
  /** Data segments rendered as donut slices. */
  readonly segments = input.required<DonutSegment[]>();
  /** Visual size of the ring. @default 'md' */
  readonly size = input<DonutSize>('md');
  /** Whether to render the total label in the center of the chart. @default true */
  readonly showTotal = input<boolean>(true);
  /** Whether to render the legend alongside the chart. @default true */
  readonly showLegend = input<boolean>(true);
  /** Legend presentation style. @default 'plain' */
  readonly legendVariant = input<DonutLegendVariant>('plain');
  /** Background stroke color used for the empty ring. @default '#e5e7eb' */
  readonly backgroundStroke = input<string>('#e5e7eb');

  readonly totalValue = computed(() => {
    return this.segments().reduce((sum, seg) => sum + seg.value, 0);
  });

  readonly sizeClass = computed(() => {
    const sizeMap = {
      sm: 'hx-donut-chart__ring--sm',
      md: 'hx-donut-chart__ring--md',
      lg: 'hx-donut-chart__ring--lg',
    };
    return sizeMap[this.size()];
  });

  readonly legendItemClasses = computed(() => {
    if (this.legendVariant() === 'split') {
      return 'flex flex-1 basis-0 flex-col items-start justify-center gap-0 min-w-0';
    }

    return '';
  });

  readonly totalLabelFontSize = computed(() => {
    const sizeMap = { sm: 8, md: 9, lg: 10 };
    return sizeMap[this.size()];
  });

  readonly totalValueFontSize = computed(() => {
    const sizeMap = { sm: 14, md: 16, lg: 18 };
    return sizeMap[this.size()];
  });

  readonly renderedSegments = computed(() => {
    const segments = this.segments();
    const total = this.totalValue();
    const circumference = 251; // 2 * π * r (r=40)

    let cumulativeOffset = 0;

    return segments.map((segment) => {
      const percentage = total > 0 ? segment.value / total : 0;
      const dashLength = circumference * percentage;
      const dashArray = `${dashLength} ${circumference}`;
      const dashOffset = -cumulativeOffset;

      cumulativeOffset += dashLength;

      return {
        ...segment,
        dashArray,
        dashOffset,
      };
    });
  });

  getPercentage(value: number): number {
    const total = this.totalValue();
    return total > 0 ? Math.round((value / total) * 100) : 0;
  }
}
